import AlertQueue from '../AlertQueue';
import MediaSound from '../MediaSound';
import UrlSound from '../UrlSound';
import PcmSound from '../PcmSound';
import giftColorThresholds from '../GiftThresholds';
import util from '../util';
import MessageWidget from './MessageWidget';

export default class GiftWidget extends MessageWidget {
	constructor(opts) {
		super(opts);

		this.queue = new AlertQueue();
		this._chatElem = null;

		// Puppet (optional, enabled via settings.puppet; only 'frog' today). The
		// puppet view + lip-sync engine are lazy-loaded on first enable so the
		// default alert widget stays light (they pull in ~1 MB of WASM).
		this._puppet = null;
		this._puppetKey = null;      // key of the currently-built puppet
		this._puppetLoadKey = null;  // key of the in-flight/last load
		this._puppetStage = null;
		this._puppetElem = null;
		this._puppetLoadPromise = null;
		this._LipsyncEngine = null;
		this._visemeAt = null;
		this._activeMouthSound = null;
		this._mouthRaf = null;
	}

	_build() {
		this._buildDOM();
		this._setupAudioMonitor();
		this.queue.onChange = () => this._updateQueueDisplay();
	}

	_audioRequired() { return !!(this.settings.enableTTS || this.settings.enableEventSounds || this.settings.enableMedia); }

	async init(initialData = {}) {
		await super.init(initialData);
		if(initialData.alerts) {
			for(const alert of initialData.alerts)
				this._onAlert(alert);
		}
	}

	destroy() {
		this._stopMouth();
		this._puppet?.destroy();
		this.queue.skip(); // abort current item — its onAbort handler stops sounds and media
		super.destroy();
	}

	// Pause all alert playback: skip the current alert (marking it queued in the DB so it
	// replays on resume), cancel all pending items from the in-memory queue, and leave the
	// queue paused.  The caller is responsible for calling queue.resume() when ready.
	stopAlerts() {
		const current = this.queue.currentItem;
		this.queue.pause();
		// queue.pause() calls the current entry's pause callback, which suspends the audio
		// context. Undo that — stopping alerts shouldn't require a click to re-enable audio.
		this._intentionalSuspend = false;
		this.soundManager.audioCtx.resume().catch(() => {});
		// Flag the item so _playAlert emits 'queued' instead of 'played' when it finishes.
		if(current) current._stoppedWhilePlaying = true;
		this.queue.cancel(() => true);
	}

	// ── Settings ──────────────────────────────────────────────────────────────

	applySettings(settings) {
		super.applySettings(settings);
		this.queue.timeoutMs = (this.settings.loadTimeout || 10000);
		this._syncPuppet();
	}

	// ── Puppet (talking frog; extensible to more puppets later) ──────────────

	_puppetActive() { return !!this.settings.puppet && !!this._puppet; }

	// Reconcile the puppet with settings.puppet ('' = none, 'frog' = talking frog).
	// Builds (and lazy-loads) the puppet on first enable; hides it when disabled.
	// The puppet view + lip-sync engine are code-split so the default alert widget
	// stays light (they pull in ~1 MB of WASM).
	// A key that changes whenever the puppet must be rebuilt: the type, plus the
	// custom SVG url when in custom mode (so re-uploading swaps the puppet).
	_desiredPuppetKey() {
		const type = this.settings.puppet || '';
		return type === 'custom' ? `custom:${this.settings.puppetSvg?.url || ''}` : type;
	}

	_syncPuppet() {
		if(!this.settings.puppet) {
			this._stopMouth();
			this._slidePuppetOut();
			this.container.classList.remove('puppet');
			if(this._puppetStage) this._puppetStage.hidden = true;
			return;
		}
		const key = this._desiredPuppetKey();
		if(this._puppet && this._puppetKey === key) {
			this.container.classList.add('puppet');
			this._puppetStage.hidden = false;
		} else {
			this._loadPuppet(key);
		}
	}

	_loadPuppet(key) {
		if(this._puppetLoadPromise && this._puppetLoadKey === key) return this._puppetLoadPromise;
		this._puppetLoadKey = key;
		return this._puppetLoadPromise = (async () => {
			this._teardownPuppet(); // type or custom SVG changed — rebuild from scratch

			// Custom mode: fetch the user's uploaded SVG. If it can't be loaded, bail
			// (alerts still play, just without a puppet).
			let svgMarkup = null;
			if(this.settings.puppet === 'custom') {
				const url = this.settings.puppetSvg?.url;
				if(!url) return;
				try {
					svgMarkup = await fetch(url).then(r => { if(!r.ok) throw new Error(`svg ${r.status}`); return r.text(); });
				} catch(e) {
					console.error('Could not load custom puppet SVG', e);
					return;
				}
			}

			const [{ default: PuppetView }, lipsync] = await Promise.all([
				import(/* webpackChunkName: "lipsync" */ '../lipsync/FrogView'),
				import(/* webpackChunkName: "lipsync" */ '../lipsync/LipsyncEngine'),
			]);
			this._LipsyncEngine = lipsync.default;
			this._visemeAt = lipsync.visemeAt;
			this._buildPuppetStage(PuppetView, svgMarkup);
			this._LipsyncEngine.preload().catch(() => {});
			this._puppetKey = key;
			// Reveal now if still enabled, and regenerate CSS to include puppet rules.
			if(this.settings.puppet) {
				this.container.classList.add('puppet');
				this._puppetStage.hidden = false;
			} else {
				this._puppetStage.hidden = true;
			}
			this._generateCSS();
		})();
	}

	_teardownPuppet() {
		this._stopMouth();
		this._puppet?.destroy?.();
		this._puppet = null;
		if(this._puppetStage) { this._puppetStage.remove(); this._puppetStage = null; }
		this.container.classList.remove('puppet');
	}

	_buildPuppetStage(PuppetView, svgMarkup) {
		// The puppet is an overlay fixed to the bottom of the widget; it does not
		// touch how alerts render. The stage just wraps the puppet body (which is
		// clipped for the peek-from-bottom look) and handles the slide transition.
		this._puppetStage = document.createElement('div');
		this._puppetStage.className = 'puppet-stage';
		this._puppetStage.hidden = true;

		this._puppetElem = document.createElement('div');
		this._puppetElem.className = 'puppet-body';
		this._puppetStage.appendChild(this._puppetElem);

		this.container.appendChild(this._puppetStage);
		this._puppet = new PuppetView(this._puppetElem, svgMarkup);
	}

	// The puppet lives off-screen (translated down) and slides up while an alert
	// is on screen, then back down once the queue drains (see _show/_hideMessage).
	_slidePuppetIn() { this._puppetStage?.classList.add('puppet-in'); }
	_slidePuppetOut() { this._puppetStage?.classList.remove('puppet-in'); }

	// Drive the mouth from `sound`'s viseme timeline, clocked by the audio
	// context so pause/resume (which suspends the context) stays in sync.
	_startMouth(sound) {
		if(!sound?._visemes || !this._puppet) return;
		this._stopMouth();
		this._activeMouthSound = sound;
		const result = sound._visemes;
		const audioCtx = this.soundManager.audioCtx;
		const t0 = audioCtx.currentTime;
		const durationSec = result.visemes.length / result.fps;
		const tick = () => {
			if(this._activeMouthSound !== sound) return;
			const elapsed = audioCtx.currentTime - t0;
			if(elapsed >= durationSec + 0.05) { this._stopMouth(sound); return; }
			this._puppet.setViseme(this._visemeAt(result, elapsed));
			this._mouthRaf = requestAnimationFrame(tick);
		};
		this._mouthRaf = requestAnimationFrame(tick);
	}

	_stopMouth(sound) {
		if(sound && this._activeMouthSound !== sound) return;
		if(this._mouthRaf) { cancelAnimationFrame(this._mouthRaf); this._mouthRaf = null; }
		this._activeMouthSound = null;
		this._puppet?.setIdle();
	}

	_emitAlertStatus(dbId, status, extra = {}) {
		if(!dbId) return;
		this.container.dispatchEvent(new CustomEvent('alertStatus', { bubbles: true, detail: { dbId, status, ...extra } }));
	}

	_buildDOM() {
		this.container.classList.add('widget', 'gift-widget');

		this.audioResumeAlert = document.createElement('div');
		this.audioResumeAlert.className = 'audio-resume-alert';
		this.audioResumeAlert.textContent = 'Click anywhere to enable audio';
		this.audioResumeAlert.hidden = true;
		this.container.appendChild(this.audioResumeAlert);

		this.playerElem = document.createElement('div');
		this.playerElem.id = 'player';
		this.container.appendChild(this.playerElem);

		this.queueStripElem = document.createElement('div');
		this.queueStripElem.className = 'queue-strip';
		this.queueStripElem.hidden = true;
		this.container.appendChild(this.queueStripElem);
	}


	_generateCSS() {
		const imageHeight = this.settings.imageHeight
			|| (this.settings.fontSize > 0 && this.settings.fontSize * 3)
			|| 100;

		const css = `
			.widget.gift-widget {
				${this.settings.width ? `max-width: ${this.settings.width}px;` : ''}
				${this.settings.marginTop ? `margin-top: ${this.settings.marginTop}px;` : ''}
			}
			.widget.gift-widget > .chatMessage {
				${this.settings.fontFamily ? `font-family: '${this.settings.fontFamily}';` : ''}
				${this.settings.fontSize > 0 ? `font-size: ${this.settings.fontSize}px;` : ''}
				${this.settings.letterSpacing > 0 ? `letter-spacing: ${this.settings.letterSpacing}px;` : ''}
				text-align: center;
				${this.settings.boldText ? `font-weight: bold;` : ''}
				${this.settings.italicText ? `font-style: italic;` : ''}
				${this.settings.underlineText || this.settings.strikethroughText ? `text-decoration: ${this.settings.underlineText ? 'underline' : ''} ${this.settings.strikethroughText ? 'line-through' : ''};` : ''}
				${this.settings.uppercaseText ? `text-transform: uppercase;` : ''}
				${this.settings.textColor ? `color: ${this.settings.textColor};` : ''}
				${this.settings.textBg ? `background-color: ${this.settings.textBg};` : ''}
			}
			.widget.gift-widget > .queue-strip {
				${this.settings.textBg ? `background-color: ${this.settings.textBg};` : ''}
				${this.settings.blurRadius > 0 || this.settings.spreadRadius > 0 ? `filter: ${util.generateSpreadShadowFilter(this.settings.spreadRadius, this.settings.blurRadius, this.settings.shadowColor)};` : ''}
			}
			.widget.gift-widget > .chatMessage > .content {
				${this.settings.blurRadius > 0 || this.settings.spreadRadius > 0 ? `filter: ${util.generateSpreadShadowFilter(this.settings.spreadRadius, this.settings.blurRadius, this.settings.shadowColor)};` : ''}
			}
			.widget.gift-widget > .chatMessage > .chatImages { display: flex; flex-wrap: wrap; justify-content: center; align-items: flex-end; gap: 4px; }
			.widget.gift-widget > .chatMessage > .chatImages > .chatImage { height: ${imageHeight}px; }
			.widget.gift-widget > .chatMessage > .chatImages:has(.chatImage:nth-child(2))  > .chatImage { height: ${imageHeight * .9}px; }
			.widget.gift-widget > .chatMessage > .chatImages:has(.chatImage:nth-child(3))  > .chatImage { height: ${imageHeight * .8}px; }
			.widget.gift-widget > .chatMessage > .chatImages:has(.chatImage:nth-child(4))  > .chatImage { height: ${imageHeight * .7}px; }
			.widget.gift-widget > .chatMessage > .chatImages:has(.chatImage:nth-child(5))  > .chatImage { height: ${imageHeight * .6}px; }
			.widget.gift-widget > .chatMessage > .chatImages:has(.chatImage:nth-child(10)) > .chatImage { height: ${imageHeight * .4}px; }
			.widget.gift-widget > .chatMessage > .chatImages:has(.chatImage:nth-child(15)) > .chatImage { height: ${imageHeight * .3}px; }
			:root { --animate-duration: ${this.settings.duration || 500}ms; }
			${this.settings.queueIconSize ? `.queue-circle { width: ${this.settings.queueIconSize}px; height: ${this.settings.queueIconSize}px; } .queue-ellipsis { line-height: ${this.settings.queueIconSize}px; font-size: ${Math.round(this.settings.queueIconSize)}px; }` : ''}
			${this.settings.extraCSS || ''}
		`;
		this._setCSS('giftWidgetStyle', css + this._puppetCSS());
		this._updateFontLink();
		this._updateQueueDisplay();
		this._repositionQueueStrip();
	}

	// Puppet CSS, appended to the gift styles only when a puppet is enabled. The
	// puppet is a bottom-centered overlay that does NOT change how alerts render.
	// `position: fixed` pins it to the viewport in the live widget and to the
	// scaled preview canvas (a transformed ancestor becomes the containing block),
	// so it's centered + bottom in both. It sits off-screen (translated down) and
	// slides up via `.puppet-in` while an alert is on screen.
	_puppetCSS() {
		const s = this.settings;
		if(!s.puppet) return '';
		const size = s.puppetSize > 0 ? s.puppetSize : 360;
		const reveal = s.puppetReveal > 0 ? s.puppetReveal : 0.67; // fraction of body shown
		return `
			.widget.gift-widget.puppet .puppet-stage {
				position: fixed;
				left: 50%;
				bottom: 0;
				width: ${size}px;
				max-width: 100%;
				transform: translate(-50%, 110%);
				transition: transform ${s.duration || 500}ms ease;
				pointer-events: none;
			}
			.widget.gift-widget.puppet .puppet-stage.puppet-in { transform: translate(-50%, 0); }
			.widget.gift-widget.puppet .puppet-stage[hidden] { display: none; }
			.widget.gift-widget.puppet .puppet-body { width: ${size}px; max-width: 100%; height: ${Math.round(size * reveal)}px; overflow: hidden; }
		`;
	}

	// Move the queue strip before or after the active alert element to match queuePosition.
	// Called on every settings change so the preview (and live widget) update immediately.
	// No-ops if there is no active message in the DOM yet.
	_repositionQueueStrip() {
		if(!this._chatElem || this._chatElem.parentNode !== this.container) return;
		if(this.settings.queuePosition === 'above') {
			if(this.queueStripElem.nextSibling !== this._chatElem)
				this.container.insertBefore(this.queueStripElem, this._chatElem);
		} else {
			if(this._chatElem.nextSibling !== this.queueStripElem)
				this.container.insertBefore(this.queueStripElem, this._chatElem.nextSibling);
		}
	}

	_onAlert(data) {
		try {
			if(!data?.message) return;
			if(data.id) data.message.dbId = data.id;

			if(this.settings.excludedChannels?.includes(`${data.message.serviceId}:${data.message.target?.channelToken}`)) {
				this._emitAlertStatus(data.id, 'ignored');
				return;
			}
			if(this.settings.minDonation && data.message?.convertedValue > 0 && data.message?.convertedValue < this.settings.minDonation) {
				this._emitAlertStatus(data.id, 'ignored');
				return;
			}

			const prepared = util.prepareMessage(data.message, {
				id: true, serviceId: true, serviceName: true, serviceIconUrl: true,
				avatarUrl: true, displayName: true, spansDom: true, giftString: true,
				eventString: true, classList: true, giftColorThreshold: true,
				sounds: true,
				group: true, groupId: true, images: true, consoleLog: true,
			}, {
				giftTypes: this.giftTypes,
				eventTypes: this.eventTypes,
				serviceIcons: this.serviceIcons,
				user: this.userData,
				giftColorThresholds,
				giftMediaThresholds: this.giftThresholds,
				textReplacements: this.textReplacements,
			});

			if(data.message?.messages) {
				prepared.messages = data.message.messages.map(m => util.prepareMessage(m, {
					serviceIconUrl: true, avatarUrl: true, displayName: true, spansDom: true,
					giftString: true, eventString: true, classList: true, giftColorThreshold: true, images: true,
				}, {
					giftTypes: this.giftTypes, eventTypes: this.eventTypes,
					serviceIcons: this.serviceIcons, user: this.userData,
					giftColorThresholds, giftMediaThresholds: this.giftThresholds,
					textReplacements: this.textReplacements,
				}));
			}

			if(prepared.group) {
				const activeItems = [this.queue.currentItem, ...this.queue.pendingItems].filter(Boolean);
				const lastInGroup = activeItems.findLast(it => it.prepared.groupId === prepared.groupId);
				if(lastInGroup) {
					lastInGroup.prepared.images?.push?.(...(prepared.images || []));
					lastInGroup.prepared.message.multiplier = (lastInGroup.prepared.multiplier || 1) + (data.message.multiplier || 1);
					lastInGroup.prepared.giftString = util.generateGiftString(lastInGroup.prepared.message, this.giftTypes?.[data.message.serviceId]?.[data.message.giftType]).displayString;
					if(lastInGroup === this.queue.currentItem && this._chatElem)
						this._addImagesToElement(this._chatElem, prepared.images || []);
					return;
				}
			}

			console.log.apply(console, prepared.consoleLog);

			const activeIds = new Set([this.queue.currentItem, ...this.queue.pendingItems].filter(Boolean).map(it => it.prepared.id));
			for(let i = 0; i < 10; i++) {
				if(!activeIds.has(prepared.id)) break;
				prepared.id = util.generateRandomString(20);
			}
			prepared.classList.push('animate__animated');

			const item = { prepared };

			this.queue.append({
				data: item,
				preload: signal => this._preloadAlert(item, signal),
				play:    signal => this._playAlert(item, signal),
				pause: () => {
					this._intentionalSuspend = true;
					this.soundManager.audioCtx.suspend();
					item.mediaSound?.pause();
				},
				resume: () => {
					this._intentionalSuspend = false;
					this.soundManager.audioCtx.resume();
					item.mediaSound?.resume();
				},
			});
		} catch(e) {
			console.error('Could not handle userWidgetAlert', data, e);
		}
	}

	async _preloadAlert(item, signal) {
		try {
			item.loading = true;
			this._updateQueueDisplay(); // show loading spinner on queue circle immediately
			const noAlert = this.settings.minDonation > 0 && item.prepared.message?.convertedValue && item.prepared.message?.convertedValue < this.settings.minDonation;
			const noTts = this.settings.ttsMinDonation > 0 && item.prepared.message?.convertedValue && item.prepared.message?.convertedValue < this.settings.ttsMinDonation;
			if(noAlert) {
				this._emitAlertStatus(item.prepared.dbId, 'ignored');
				this.queue.cancel(i => i === item);
				return;
			}

			const loadSounds = [];
			const loadSoundsRecursive = async sounds => {
				try {
					if(Array.isArray(sounds)) {
						for(let sound of sounds) {
							if(await loadSoundsRecursive(sound)) return true;
						}
						throw new Error('Could not load a sound');
					}

					if(sounds.type == 'any') {
						let loaded = 0;
						for(let sound of sounds.items) {
							if(await loadSoundsRecursive(sound)) loaded++;
						}
						if(!loaded) throw new Error('Could not load any sound');
						return true;
					}

					let category = 'event';
					if(sounds.type == 'tts') {
						if(!this.settings.enableTTS) return false;
						if(!this.settings.enableTTSProlog && sounds.isProlog) return false;
						if(noTts) return false;
						category = sounds.isProlog ? 'prolog' : 'tts';
					} else if(sounds.type == 'song') {
						if(!this.settings.enableMedia) return false;
						category = 'sound';
					} else {
						if(!this.settings.enableEventSounds) return false;
					}

					let p;
					if(sounds.url) p = new UrlSound(this.soundManager, this.soundCache, sounds.proxy ? `${this.baseUrl}/proxy/${encodeURIComponent(sounds.url)}` : sounds.url);
					else if(sounds.type == 'tts') {
						const { voiceApi } = this;
						p = new PcmSound(this.soundManager, this.soundCache,
							signal => voiceApi.synthesizeSpeech(sounds.speak, sounds.voice&&sounds.provider||'vampiTTS', sounds.provider&&sounds.voice||'IVONA 2 Voice Brian22', sounds.rate, sounds.pitch, sounds.style, sounds.role, signal));
					} else if(sounds.type == 'mml') {
						const { mmlApi } = this;
						p = new PcmSound(this.soundManager, this.soundCache,
							signal => mmlApi.synthesizeMusic(sounds.mml, sounds.instrument, signal));
					} else if(sounds.type == 'song') {
						p = new MediaSound(this.playerElem.id, sounds.song);
					} else {
						if(!p) throw new Error(`Unknown sound type ${sounds.type}`);
					}
					p.category = category;
					await p.load(signal);
					loadSounds.push(p);
					return true;
				} catch(e) {
					console.error('Could not load sound', sounds, e);
					return false;
				}
			};
			await Promise.all([
				(async () => {
					try {
						await loadSoundsRecursive(item.prepared.sounds);
						item.sounds = loadSounds;
					} catch(e) {
						console.error('Could not load any sounds', e);
					}
				})(),
				(async () => {
					item.chatElem = this._createMessageElement(item.prepared);
					await util.awaitMediaLoadedRecursive(item.chatElem);
				})(),
			])

			// When a puppet is on, analyze each speech sound (TTS / prolog) into a
			// viseme timeline and wire it to the puppet's mouth.
			if(this.settings.puppet) {
				await this._loadPuppet(this._desiredPuppetKey());
				for(const s of item.sounds || []) {
					if(s.category !== 'tts' && s.category !== 'prolog') continue;
					const buffer = s.bufferSource?.buffer;
					if(!buffer) continue;
					try {
						s._visemes = await this._LipsyncEngine.analyze(buffer.getChannelData(0), buffer.sampleRate);
					} catch(e) {
						console.error('Lip-sync analysis failed', e);
						continue;
					}
					if(!s._visemes.visemes.length) continue;
					s.addEventListener('playing', () => this._startMouth(s), { once: true });
					s.addEventListener('ended', () => this._stopMouth(s), { once: true });
					s.addEventListener('error', () => this._stopMouth(s), { once: true });
				}
			}
		} finally {
			item.loading = false;
			this._updateQueueDisplay();
			if(this.queue.pendingItems[0] === item) this._lingerAbort?.abort();
		}
	}

	async _playAlert(item, signal) {
		try {
			const noAlert = this.settings.minDonation > 0 && item.prepared.message?.convertedValue && item.prepared.message?.convertedValue < this.settings.minDonation;
			const noTts = this.settings.ttsMinDonation > 0 && item.prepared.message?.convertedValue && item.prepared.message?.convertedValue < this.settings.ttsMinDonation;
			if(noAlert) {
				this._emitAlertStatus(item.prepared.dbId, 'skipped');
				return;
			}

			this._emitAlertStatus(item.prepared.dbId, 'playing');

			this._showMessage(item.chatElem);

			await Promise.all([
				util.setTimeoutPromise(this.settings.alertDuration || 3000, signal).catch(() => {}),
				(async () => {
					let anyError = false;
					for(const s of item.sounds || []) {
						try {
							if(s.category == 'tts' && (!this.settings.enableTTS || noTts)) continue;
							if(s.category == 'prolog' && (!this.settings.enableTTS || !this.settings.enableTTSProlog && s.isProlog || noTts)) continue;
							if(s.category == 'event' && !this.settings.enableEventSounds) continue;
							if(s.category == 'song' && !this.settings.enableMedia) continue;
							if(s.constructor.name == 'MediaSound') {
								s.addEventListener('error', ev => {
									anyError = true;
									this._emitAlertStatus(item.prepared.dbId, 'error', { err: ev.detail });
								});
							}
							await s.play(signal);
						} catch(e) {
							console.error('Could not play sound', s, e);
							anyError = e;
						}
						if(anyError) continue;
					}
				})(),
			]);

			if(this.queue.pendingItems.length === 0 && !signal.aborted) {
				const lingerAbort = this._lingerAbort = new AbortController();
				signal.addEventListener('abort', () => lingerAbort.abort(), { once: true });
				await util.setTimeoutPromise(this.settings.linger || 0, lingerAbort.signal).catch(() => {});
				this._lingerAbort = null;
			}

			await this._hideMessage(item.chatElem);

			// Emit final status after everything has settled so there is no race with
			// in-flight 'played' API calls when stop is called mid-alert.
			if(signal.aborted) {
				if(item._stoppedWhilePlaying) this._emitAlertStatus(item.prepared.dbId, 'queued');
				else this._emitAlertStatus(item.prepared.dbId, 'skipped');
			} else {
				this._emitAlertStatus(item.prepared.dbId, 'played');
			}
		} catch(e) {
			console.error('_playAlert', e);
		}
	}

	_showMessage(elem) {
		// Alerts render exactly as the normal alert widget; the puppet (if any) is
		// just a bottom overlay that slides up while an alert is on screen.
		if(this.settings.inTransition) {
			elem.classList.add('animate__animated', `animate__${this.settings.inTransition}`);
		}
		if(this.settings.queuePosition === 'above') {
			this.container.appendChild(elem);
		} else {
			this.container.insertBefore(elem, this.queueStripElem);
		}
		this._chatElem = elem;
		if(this._puppetActive()) this._slidePuppetIn();
	}

	async _hideMessage(elem) {
		if(!elem) return;
		if(this._puppetActive()) this._stopMouth(); // mouth returns to idle when the alert ends
		if(this.settings.outTransition) {
			if(this.settings.inTransition) elem.classList.remove(`animate__${this.settings.inTransition}`);
			elem.classList.add('animate__animated', `animate__${this.settings.outTransition}`);
			// Wait for the CSS animation to complete; fall back to the configured duration
			// + a small buffer in case animationend never fires (e.g. display:none).
			await new Promise(resolve => {
				const done = () => { clearTimeout(fallback); resolve(); };
				const fallback = setTimeout(done, (this.settings.duration || 500) + 500);
				elem.addEventListener('animationend', done, { once: true });
			});
		}
		elem.remove();
		if(this._chatElem === elem) this._chatElem = null;
		// Slide the puppet back down once the queue has drained.
		if(this._puppetActive() && this.queue.pendingItems.length === 0) this._slidePuppetOut();
	}

	_createMessageElement(prepared) {
		const div = document.createElement('div');
		div.className = 'gift chatMessage';
		for(const cls of (prepared.classList || [])) div.classList.add(cls);

		if(prepared.images?.length) {
			const imagesDiv = document.createElement('div');
			imagesDiv.className = 'chatImages';
			for(const img of prepared.images) {
				if(!img?.url) continue;
				const imgElem = document.createElement('img');
				imgElem.src = img.url;
				imgElem.alt = '';
				imgElem.className = 'chatImage';
				imagesDiv.appendChild(imgElem);
			}
			div.appendChild(imagesDiv);
		}

		div.appendChild(this._createMessageContent(prepared));
		return div;
	}

	_addImagesToElement(elem, images) {
		if(!images?.length) return;
		let imagesDiv = elem.querySelector('.chatImages');
		if(!imagesDiv) {
			imagesDiv = document.createElement('div');
			imagesDiv.className = 'chatImages';
			elem.prepend(imagesDiv);
		}
		for(const img of images) {
			if(!img?.url) continue;
			const imgElem = document.createElement('img');
			imgElem.src = img.url;
			imgElem.alt = '';
			imgElem.className = 'chatImage';
			imagesDiv.appendChild(imgElem);
		}
	}

	_createMessageContent(prepared) {
		const content = document.createElement('div');
		content.className = 'content';

		if(prepared.serviceIconUrl) {
			const icon = document.createElement('img');
			icon.src = prepared.serviceIconUrl;
			icon.alt = prepared.serviceName || '';
			icon.className = 'service-icon';
			content.appendChild(icon);
		} else if(prepared.serviceName) {
			const small = document.createElement('small');
			small.className = 'platform';
			small.textContent = prepared.serviceName;
			content.appendChild(small);
		}

		if(prepared.avatarUrl) {
			const avatar = document.createElement('img');
			avatar.src = prepared.avatarUrl;
			avatar.alt = '';
			avatar.className = 'avatar';
			content.appendChild(avatar);
		}

		const name = document.createElement('b');
		name.textContent = prepared.displayName;
		content.appendChild(name);

		if(prepared.giftString) {
			const giftElem = document.createElement('b');
			giftElem.textContent = ` ${prepared.giftString}`;
			content.appendChild(giftElem);
		}

		if(prepared.eventString) {
			const eventElem = document.createElement('span');
			eventElem.textContent = ` ${prepared.eventString}`;
			content.appendChild(eventElem);
		}

		if(prepared.spansDom?.length) {
			const spansElem = document.createElement('span');
			spansElem.appendChild(document.createTextNode(': '));
			for(const node of prepared.spansDom) spansElem.appendChild(node);
			content.appendChild(spansElem);
		}

		if(prepared.messages?.length) {
			const messagesDiv = document.createElement('div');
			messagesDiv.className = 'messages';
			for(const m of prepared.messages) {
				const mDiv = document.createElement('div');
				mDiv.className = 'message';
				mDiv.appendChild(this._createMessageContent(m));
				messagesDiv.appendChild(mDiv);
			}
			content.appendChild(messagesDiv);
		}

		return content;
	}

	_collectImageUrls(prepared) {
		const urls = new Set();
		if(prepared.avatarUrl) urls.add(prepared.avatarUrl);
		if(prepared.serviceIconUrl) urls.add(prepared.serviceIconUrl);
		for(const img of (prepared.images || [])) {
			if(img?.url) urls.add(img.url);
		}
		for(const node of (prepared.spansDom || [])) {
			if((node.nodeName === 'IMG' || node.nodeName === 'VIDEO') && node.src) urls.add(node);
		}
		for(const m of (prepared.messages || [])) {
			for(const url of this._collectImageUrls(m)) urls.add(url);
		}
		return [...urls];
	}

	_updateQueueDisplay() {
		const pending = this.queue.pendingItems;
		const showStrip = this.settings.queuePosition !== 'hidden' && pending.length > 0;
		this.queueStripElem.hidden = !showStrip;
		this.queueStripElem.innerHTML = '';
		if(!showStrip) return;

		const maxCount = this.settings.queueIconCount || 5;
		const shown = pending.slice(0, maxCount);
		const overflow = pending.length - shown.length;
		const isNextUp = !this._chatElem;

		shown.forEach((item, idx) => {
			this.queueStripElem.appendChild(this._createQueueCircle(item, isNextUp && idx === 0));
		});

		if(overflow > 0) {
			const ellipsis = document.createElement('div');
			ellipsis.className = 'queue-ellipsis';
			ellipsis.textContent = `+${overflow}`;
			this.queueStripElem.appendChild(ellipsis);
		}
	}

	_createQueueCircle(item, isNext) {
		const circle = document.createElement('div');
		circle.className = 'queue-circle';
		if(isNext) circle.classList.add('next');
		if(item.loading) circle.classList.add('loading');

		const iconUrl = item.prepared.avatarUrl || item.prepared.serviceIconUrl;
		if(iconUrl) {
			const img = document.createElement('img');
			img.src = iconUrl;
			img.alt = '';
			circle.appendChild(img);
		}

		return circle;
	}
}
